import {makeObservable} from 'mobx';

import {api} from '../../api/proto';
import {randomUint8Array} from '../../utils/arrayUtils';
import {AppStore} from '../AppStore';
import BaseTagsStore from './BaseTagsStore';
import ITag from './ITag';
import TagEvent from './TagEvent';
import getCreateWorkspaceTagError from './utils/getCreateWorkspaceTagError';
import {TagType} from './TagType';

export class WorkspaceTagsStore extends BaseTagsStore {
  constructor(public app: AppStore) {
    super(app, TagType.Workspace);

    makeObservable(this);
  }

  public load = async () => {
    if (this.loading) {
      return;
    }
    this.setLoading(true);

    const {error, res} = await this.app.activeWorkspace.workspaceRequest<api.IWorkspaceGetTagsResponse>(
      {
        getTags: new api.WorkspaceGetTagsRequest({
          workspaceId: this.app.activeWorkspace.id,
        }),
      },
      'getTags',
    );
    this.setLoading(false);

    if (res?.tags) {
      this.processList(res?.tags);
    }

    return {error, res};
  };

  public create = async (name: string, color: string) => {
    const {error, res} = await this.app.activeWorkspace.workspaceRequest<api.IWorkspaceCreateTagResponse>(
      {
        createTag: new api.WorkspaceCreateTagRequest({
          workspaceId: this.app.activeWorkspace.id,
          tag: {
            tag: name,
            color,
          },
        }),
      },
      'createTag',
    );

    const errorMessage = getCreateWorkspaceTagError(error, res);
    if (!errorMessage) {
      this.processNewTag({
        localID: randomUint8Array(),
        tag: name,
        color,
        workspaceID: this.app.activeWorkspace.id,
        type: this.type,
      });
      this.emit(TagEvent.CREATE_NEW_TAG, this);
    }

    return {error, res};
  };

  public update = async (tag: ITag) => {
    const {error, res} = await this.app.activeWorkspace.workspaceRequest<api.IWorkspaceUpdateTagResponse>(
      {
        updateTag: new api.WorkspaceUpdateTagRequest({
          workspaceId: this.app.activeWorkspace.id,
          tag,
        }),
      },
      'updateTag',
    );

    if (!res?.status) {
      this.processEditedTag(tag);
      this.emit(TagEvent.UPDATE_TAG, this);
    }

    return {error, res};
  };

  public delete = async (tag: ITag) => {
    const {error, res} = await this.app.activeWorkspace.workspaceRequest<api.IWorkspaceDeleteTagResponse>(
      {
        deleteTag: new api.WorkspaceDeleteTagRequest({
          workspaceId: this.app.activeWorkspace.id,
          tag,
        }),
      },
      'deleteTag',
    );

    if (!res?.status) {
      this.processDeletedTag(tag);
      this.emit(TagEvent.DELETE_TAG, this);
    }

    return {error, res};
  };

  public reorderTags = async (tagIds: Uint8Array[]) => {
    const {error, res} = await this.app.activeWorkspace.workspaceRequest<api.IWorkspaceReorderTagsResponse>(
      {
        reorderTags: new api.WorkspaceReorderTagsRequest({
          workspaceId: this.app.activeWorkspace.id,
          ids: tagIds,
        }),
      },
      'reorderTags',
    );

    if (!res?.status) {
      this.processReorderTags(tagIds);
    }

    return {error, res};
  };
}

export default WorkspaceTagsStore;
